import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../api/auth.api.js';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('lms_user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState(() => localStorage.getItem('lms_token') || null);
  const [loading, setLoading] = useState(true);

  // Khởi tạo: kiểm tra tính hợp lệ của token
  useEffect(() => {
    async function verifyToken() {
      const savedToken = localStorage.getItem('lms_token');
      if (savedToken) {
        try {
          const res = await authApi.getMe();
          if (res?.data) {
            setUser(res.data);
            localStorage.setItem('lms_user', JSON.stringify(res.data));
          }
        } catch (err) {
          console.warn('Phiên đăng nhập không hợp lệ hoặc đã hết hạn.');
          localStorage.removeItem('lms_token');
          localStorage.removeItem('lms_user');
          setUser(null);
          setToken(null);
        }
      }
      setLoading(false);
    }

    verifyToken();
  }, []);

  const login = useCallback(async (credentials) => {
    const res = await authApi.login(credentials);
    const { token: newToken, user: newUser } = res.data;

    localStorage.setItem('lms_token', newToken);
    localStorage.setItem('lms_user', JSON.stringify(newUser));

    setToken(newToken);
    setUser(newUser);
    return newUser;
  }, []);

  const register = useCallback(async (formData) => {
    const res = await authApi.register(formData);
    const { token: newToken, user: newUser } = res.data;

    localStorage.setItem('lms_token', newToken);
    localStorage.setItem('lms_user', JSON.stringify(newUser));

    setToken(newToken);
    setUser(newUser);
    return newUser;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('lms_token');
    localStorage.removeItem('lms_user');
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((updatedUserData) => {
    setUser((prev) => {
      const merged = { ...prev, ...updatedUserData };
      localStorage.setItem('lms_user', JSON.stringify(merged));
      return merged;
    });
  }, []);

  // Tải lại hồ sơ (kèm gói đang dùng) từ máy chủ — dùng sau khi thanh toán xong để mở khóa ngay, không cần đăng nhập lại
  const refreshUser = useCallback(async () => {
    try {
      const res = await authApi.getMe();
      if (res?.data) {
        setUser(res.data);
        localStorage.setItem('lms_user', JSON.stringify(res.data));
      }
    } catch {
      // Lỗi mạng tạm thời: giữ thông tin cũ, các trang tự hỏi lại quyền khi mở
    }
  }, []);

  // ADMIN có toàn quyền; INSTRUCTOR chỉ quản lý khóa học của mình (YC-ADM-09)
  const isAdmin = user?.role === 'ADMIN';
  const isInstructor = user?.role === 'INSTRUCTOR';
  const canManageContent = isAdmin || isInstructor;

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    isAdmin,
    isInstructor,
    canManageContent,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
